import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, split } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloClient as RscCompatibleApolloClient,
  InMemoryCache as RscCompatibleInMemoryCache,
} from '@apollo/client-integration-nextjs';
import {
  NEXT_PUBLIC_GRAPHQL_API,
  NEXT_PUBLIC_GRAPHQL_WS_API,
  NEXT_PUBLIC_RELEASE_VERSION,
} from '@env';
import { getOrSetViewerId } from '@modules/analytics/helpers';
import { getToken } from '@modules/auth-helpers';
import { createClient } from 'graphql-ws';
import { map } from 'rxjs';

const apolloLogger = new ApolloLink((operation, forward) => {
  const startTime = Date.now();
  return forward(operation).pipe(
    map((result) => {
      const elapsed = Date.now() - startTime;
      const group = [`[GraphQL ${operation.operationName}] (${elapsed}ms)`];
      console.groupCollapsed(...group);
      console.log('INIT', operation);
      console.log('RESULT', result);
      console.groupEnd();
      return result;
    }),
  );
});

export { gql } from '@apollo/client';

// import { BatchHttpLink } from '@apollo/client/link/batch-http';

// const httpLink = new BatchHttpLink({
//   uri: NEXT_PUBLIC_GRAPHQL_API,
// });

const httpLink = new HttpLink({
  uri: NEXT_PUBLIC_GRAPHQL_API,
  headers: {
    'X-Blinq-Client': `WebDashboard, ${NEXT_PUBLIC_RELEASE_VERSION}, ${process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN}`,
  },
});

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error) => {
      if (error.message === 'INVALID_TOKEN') {
        return true;
      }

      return false;
    },
  },
});

const authLink = new SetContextLink(async ({ headers, isUnauthenticatedRequest }, operation) => {
  if (typeof window === 'undefined') {
    return {
      headers,
    };
  }

  const token = await getToken();

  // Get an authorization header if it was set in the headers object. This would happen at the time of calling the query/mutation.
  // i.e. useMyQuery({ context: { headers: { authorization: "Bearer <token>" } } });
  const authorizationHeaderOverride = getAuthorizationHeaderOverride(headers);

  if (!token && !isUnauthenticatedRequest && !authorizationHeaderOverride) {
    throw new Error('INVALID_TOKEN');
  }

  const authorizationHeader = token ? `Bearer ${token}` : '';
  return {
    headers: {
      ...headers,
      authorization: authorizationHeaderOverride
        ? authorizationHeaderOverride
        : authorizationHeader,
      'X-APOLLO-OPERATION-NAME': operation.operationName,
      // schema for this header can be found here:
      // https://www.notion.so/blinq-me/Authentication-API-Contract-2c669a35ad8f80659554d8a9cba002e4?source=copy_link
      // the viewer id is what we use as the device id across the app
      'X-Blinq-Client': `WebDashboard, ${NEXT_PUBLIC_RELEASE_VERSION}, ${process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN}, ${getOrSetViewerId() || ''}`,
    },
  };
});

/** Fetches an authorization header value from the headers object if it exists */
function getAuthorizationHeaderOverride(headers: Record<string, unknown>): string | undefined {
  const authHeader = headers?.authorization;

  if (typeof authHeader === 'string') {
    return authHeader;
  }

  return '';
}

const websocketLink =
  typeof window !== 'undefined'
    ? new GraphQLWsLink(
        createClient({
          url: NEXT_PUBLIC_GRAPHQL_WS_API,
          connectionParams: async () => {
            const token = await getToken();
            return {
              Authorization: token ? `Bearer ${token}` : '',
              // Browsers cannot set headers on a WebSocket handshake, so the client identity the
              // HttpLink sends as a header travels in the connection payload instead.
              'X-Blinq-Client': `WebDashboard, ${NEXT_PUBLIC_RELEASE_VERSION}, ${process.env.NEXT_PUBLIC_DEPLOYMENT_DOMAIN}, ${getOrSetViewerId() || ''}`,
            };
          },
        }),
      )
    : null;

const splitLink =
  typeof window !== 'undefined' && websocketLink !== null
    ? split(
        ({ query }) => {
          const def = getMainDefinition(query);
          return def.kind === 'OperationDefinition' && def.operation === 'subscription';
        },
        websocketLink,
        httpLink,
      )
    : httpLink;

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    ...(process.env.NODE_ENV === 'development' ? [apolloLogger] : []),
    retryLink,
    authLink,
    splitLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      CrmIntegration: {
        keyFields: ['integration_id'],
      },
      // monday group ids are per board ("topics" on most boards), so they must
      // not be normalized as entities or one board's groups overwrite another's.
      MondayBoardGroup: {
        keyFields: false,
      },
      UnifiedProfile: {
        fields: {
          design: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
          images: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
        },
      },
      Card: {
        fields: {
          properties: { merge: true },
        },
      },
      Workspace: {
        fields: {
          tags: { merge: true },
        },
      },
      Query: {
        fields: {
          getProfilesWithNoCards: {
            merge(_, incoming: []) {
              return [...incoming];
            },
          },
        },
      },
    },
  }),
});

export const rscCompatibleApolloClient = new RscCompatibleApolloClient({
  link: ApolloLink.from([
    ...(process.env.NODE_ENV === 'development' ? [apolloLogger] : []),
    retryLink,
    authLink,
    splitLink,
  ]),
  cache: new RscCompatibleInMemoryCache({
    typePolicies: {
      CrmIntegration: {
        keyFields: ['integration_id'],
      },
      // monday group ids are per board ("topics" on most boards), so they must
      // not be normalized as entities or one board's groups overwrite another's.
      MondayBoardGroup: {
        keyFields: false,
      },
      UnifiedProfile: {
        fields: {
          design: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
          images: {
            merge(existing, incoming, { mergeObjects }) {
              return mergeObjects(existing, incoming);
            },
          },
        },
      },
      Card: {
        fields: {
          properties: { merge: true },
        },
      },
      Workspace: {
        fields: {
          tags: { merge: true },
        },
      },
      Query: {
        fields: {
          getProfilesWithNoCards: {
            merge(_, incoming: []) {
              return [...incoming];
            },
          },
        },
      },
    },
  }),
});
